var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { render } from 'react-dom';
import PropTypes from 'prop-types';
import api from '@utils/axios';
import { serverErrorMessage } from '@utils/serverErrorMessage';
import { translate } from '@utils/i18n';
import Table from '@components/Table';
import ConfirmDialog from '@components/ConfirmDialog/ConfirmDialog';
import { ConfirmDialog as UiConfirmDialog } from '@ui/molecules/ConfirmDialog';
import { Radio } from '@ui/molecules/inputs';
import Banner, { BannerVariant } from '@components/Banner';
import LoaderToast from '@components/Loader/LoaderToast';
import { Button } from '@ui';
import { useStableCellRenderers } from '@features/Attachments/hooks/useStableCellRenderers';
import { SubcontractorDocumentsSurface } from '@features/SubcontractorDocuments/SubcontractorDocumentsSurface';
import { bulkUpdateDocuments, DOCUMENT_UPDATE_ACTION } from '@features/SubcontractorDocuments/services/subcontractorDocumentsApi';
import useDocuments from './hooks/useDocuments';
import PrimeDocumentSection from './components/PrimeDocumentSection';
import PrimeAddDocumentsDrawer from './components/PrimeAddDocumentsDrawer';
import toUiColumns from './utils/toUiColumns';
import AddDocumentModal from './AddDocumentModal';
import { apiInternalInspectionDocumentsPath, apiInternalWorkOrderDocumentsPath, deficienciesAiPath, markAsPublicApiInternalInspectionDocumentsPath, markAsPublicApiInternalWorkOrderDocumentsPath } from '../../routes';
import getDocumentColumns from './utils/getDocumentColumns';
import './DocumentList.scss';
import { DEFAULT_PORTAL_CATEGORY } from './types';
import DeficienciesAi from './components/DeficienciesAI';
const namespace = 'features.documents';
function DocSection({ subcontractor, onRequestDelete, onRequestScan, onToggleShowOnReport, idLoading, canDelete, onOpenDeficiencies, aiEnabled, isPrime, onToggleSelect, selectedIds, canPublish }) {
    const [expanded, setExpanded] = useState(true);
    return (React.createElement("section", { className: `workspace__section documentListContainer ${expanded ? '--expanded  --active' : '--collapsed'}r` },
        React.createElement("button", { type: "button", className: "section__title", onClick: () => setExpanded(!expanded) },
            React.createElement("h2", { className: "section__label" }, subcontractor.name),
            React.createElement("hr", { className: "section__split" })),
        React.createElement("article", { className: "section__content" },
            React.createElement(Table, { columns: getDocumentColumns({
                    onRequestDelete,
                    onRequestScan,
                    onToggleShowOnReport,
                    idLoading,
                    canDelete,
                    aiEnabled,
                    onOpenDeficiencies,
                    isPrime,
                    onToggleSelect,
                    selectedIds,
                    canPublish
                }), data: subcontractor.docs, isGrouped: true }))));
}
function DocumentsList({ isPrime, workOrderId, canDelete, inspectionId, canCreate, canSettings, autoScanAi, aiEnabled, subcontractors, canPublish, directUploadsEnabled = false, documentGroups, parentType, parentId }) {
    const { addModalInfo, groupedDocuments, documentsType, confirmModalInfo, idLoading, isAIProcessing, groupedPrimeDocuments, documentDetail, pendingDocsForReview, onAddDocument, onFinishReviewDeficiencies, onRequestDelete, onRequestScan, onRequestAdd, onToggleShowOnReport, closeConfirmModal, closeAddModal, setDocumentDetail, refreshDocuments } = useDocuments({
        subcontractors,
        workOrderId,
        inspectionId,
        isPrime,
        aiEnabled,
        directUploadsEnabled,
        parentType,
        parentId
    });
    const [selectedIds, setSelectedIds] = useState([]);
    // Which group's Add was clicked on the prime side: the document type to bind the drawer to, and
    // the subcontractor section it was clicked in. Null keeps the drawer unmounted.
    const [primeAddTarget, setPrimeAddTarget] = useState(null);
    const [showBulkPublishConfirm, setShowBulkPublishConfirm] = useState(false);
    const [bulkPublishCategory, setBulkPublishCategory] = useState(DEFAULT_PORTAL_CATEGORY);
    const onToggleSelect = useCallback((id, selected) => {
        setSelectedIds((prev) => {
            if (selected) {
                return prev.includes(id) ? prev : [...prev, id];
            }
            return prev.filter((i) => i !== id);
        });
    }, []);
    const onBulkPublish = (e) => {
        e.preventDefault();
        setShowBulkPublishConfirm(true);
    };
    const publishSelected = () => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (directUploadsEnabled && parentType && parentId) {
            yield bulkUpdateDocuments({ parentType, parentId }, {
                updateAction: DOCUMENT_UPDATE_ACTION.markAsPublic,
                ids: selectedIds,
                isPublic: true,
                portalCategory: bulkPublishCategory
            });
            return;
        }
        const markAsPublicUrl = inspectionId
            ? markAsPublicApiInternalInspectionDocumentsPath(inspectionId)
            : markAsPublicApiInternalWorkOrderDocumentsPath(workOrderId);
        const { error } = yield api.patch(markAsPublicUrl, {
            ids: selectedIds,
            portal_category: bulkPublishCategory
        });
        // The legacy endpoint answers 200 with an `error` body rather than an error status.
        if (error)
            throw new Error((_a = error === null || error === void 0 ? void 0 : error.errors) === null || _a === void 0 ? void 0 : _a.join('\n'));
    });
    const doBulkPublish = () => __awaiter(this, void 0, void 0, function* () {
        try {
            yield publishSelected();
            renderBanner(translate('mark_documents_public.success_message', { namespace }), BannerVariant.Success);
        }
        catch (err) {
            renderBanner(serverErrorMessage(err, translate('mark_documents_public.failure_message', { namespace })), BannerVariant.Error);
        }
        setSelectedIds([]);
        setBulkPublishCategory(DEFAULT_PORTAL_CATEGORY);
        refreshDocuments();
    });
    const portalCategoryOptions = useMemo(() => [
        {
            value: 'inspection_report',
            label: translate('publish_category_inspection_report', { namespace })
        },
        { value: 'service_report', label: translate('publish_category_service_report', { namespace }) },
        { value: 'miscellaneous', label: translate('publish_category_other', { namespace }) }
    ], []);
    const renderBanner = (text, type) => {
        const alertContainer = document.getElementById('react-alerts-container');
        if (!alertContainer)
            return;
        render(React.createElement(Banner, { text: text, variant: type }), alertContainer);
    };
    const typesFiltered = useMemo(() => documentsType === null || documentsType === void 0 ? void 0 : documentsType.filter((type) => {
        return (!type.ai_powered ||
            aiEnabled ||
            groupedPrimeDocuments
                .flatMap((d) => d.docs)
                .flatMap((d) => d.data)
                .some((d) => d.document_type_id === type.id));
    }), [documentsType, groupedPrimeDocuments, aiEnabled]);
    // Both halves have to be present: the flag alone says nothing about whether this mount was
    // given the server-resolved groups, and an empty surface would replace a working legacy one.
    const showDirectUploadSurface = directUploadsEnabled && !!documentGroups && !!parentType && !!parentId;
    // The prime keeps every column and action it has today (requester decision 2026-10-27); only the
    // chrome changes, from a hand-rolled collapsible section + legacy grouped table to the Quimby
    // Accordion + the @ui Table's accordion-grouped presentation. Selection moves to the @ui Table's
    // own checkbox column, so the hand-rolled one is not asked for here.
    // On the direct-upload path the prime's group Add action opens the SAME drawer the
    // subcontractor side uses, not the legacy multipart modal. The hook builds the action (label,
    // icon, and when it is offered at all); only where the click goes changes.
    const primeSubcontractors = useMemo(() => {
        if (!directUploadsEnabled)
            return groupedPrimeDocuments;
        return groupedPrimeDocuments.map((subcontractor) => (Object.assign(Object.assign({}, subcontractor), { docs: subcontractor.docs.map((group) => group.action
                ? Object.assign(Object.assign({}, group), { action: Object.assign(Object.assign({}, group.action), { onClick: () => setPrimeAddTarget({
                            documentTypeId: group.documentTypeId,
                            uploaderTenantId: subcontractor.tenantId
                        }) }) }) : group) })));
    }, [directUploadsEnabled, groupedPrimeDocuments]);
    const primeAddGroup = useMemo(() => { var _a; return (_a = documentGroups === null || documentGroups === void 0 ? void 0 : documentGroups.find((group) => group.documentTypeId === (primeAddTarget === null || primeAddTarget === void 0 ? void 0 : primeAddTarget.documentTypeId))) !== null && _a !== void 0 ? _a : null; }, [documentGroups, primeAddTarget]);
    // Through `useStableCellRenderers`, the same way `AttachmentsSurface` does (task-11). Without it
    // `getCellTemplate` receives a new renderer FUNCTION as the element type on every rebuild, and a
    // new type unmounts and remounts every cell -- which on this tab also means the 10-second poll
    // restarting every PDF preview and dropping an open inline edit (release review of PR #23938).
    //
    // The prime table was the one table that did not go through it; `onToggleShowOnReport` below is
    // now stable too, but the hook is what makes the class of bug impossible rather than this one
    // instance of it.
    const primeColumnDefinitions = useMemo(() => toUiColumns(getDocumentColumns({
        onRequestDelete,
        onRequestScan,
        onToggleShowOnReport,
        idLoading,
        canDelete,
        aiEnabled,
        onOpenDeficiencies: setDocumentDetail,
        isPrime,
        canPublish
    })), [idLoading, canDelete, aiEnabled, isPrime, canPublish, onToggleShowOnReport]);
    const primeColumns = useStableCellRenderers(primeColumnDefinitions);
    const onCloseBrushAway = useCallback(() => {
        setDocumentDetail(null);
    }, []);
    useEffect(() => {
        const alertContainer = document.getElementById('react-alerts-container');
        if (!alertContainer)
            return;
        render(pendingDocsForReview > 0 && isPrime && aiEnabled ? (React.createElement(Banner, { text: translate('pending_docs_review', { namespace, count: pendingDocsForReview }), variant: BannerVariant.Info })) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        React.createElement(React.Fragment, null)), alertContainer);
    }, [pendingDocsForReview, isPrime]);
    return (React.createElement(React.Fragment, null,
        React.createElement(LoaderToast, { show: isAIProcessing && isPrime, closable: true }),
        isPrime && aiEnabled && canSettings && (React.createElement("div", { className: "documentAiSettings" },
            autoScanAi && translate('autorun', { namespace }),
            !autoScanAi && translate('manual', { namespace }),
            "\u00A0",
            React.createElement("a", { href: deficienciesAiPath(), target: "_blank", rel: "noreferrer" },
                autoScanAi && translate('scan_ai_link_auto', { namespace }),
                !autoScanAi && translate('scan_ai_link_manual', { namespace })))),
        canPublish && (React.createElement("div", { className: "qmb-content-actions documentActions" },
            React.createElement("div", { className: "content-actions__group" },
                React.createElement("button", { type: "button", className: "qmb-control js-bulk-action", onClick: onBulkPublish, disabled: selectedIds.length === 0 },
                    React.createElement("i", { className: "fa-light fa-share-from-square" }),
                    translate('publish_to_customer_portal', { namespace }))))),
        !isPrime && showDirectUploadSurface && (React.createElement(SubcontractorDocumentsSurface, { groups: documentGroups !== null && documentGroups !== void 0 ? documentGroups : [], parentType: parentType !== null && parentType !== void 0 ? parentType : '', parentId: parentId !== null && parentId !== void 0 ? parentId : 0 })),
        !isPrime &&
            !showDirectUploadSurface &&
            typesFiltered.map((documentType) => groupedDocuments[documentType.id].length > 0 || canCreate ? (React.createElement("section", { key: documentType.id, className: "workspace__section documentListContainer" },
                React.createElement("header", { className: "section__title" },
                    React.createElement("h2", { className: "section__label" },
                        documentType.title,
                        documentType.is_required && React.createElement("span", { className: "requiredFile" }, " *")),
                    React.createElement("hr", { className: "section__split" })),
                React.createElement("article", { className: "section__content" },
                    groupedDocuments[documentType.id].length > 0 && (React.createElement(Table, { columns: getDocumentColumns({
                            onRequestDelete,
                            onToggleShowOnReport,
                            idLoading,
                            canDelete,
                            aiEnabled,
                            isPrime,
                            selectedIds,
                            onToggleSelect,
                            canPublish
                        }), data: groupedDocuments[documentType.id] })),
                    canCreate && (!documentType.ai_powered || aiEnabled) && (React.createElement(Button, { onClick: () => onRequestAdd(documentType), variant: "highlighted", className: "full-size" },
                        React.createElement("i", { className: "fa-light fa-circle-plus" }),
                        translate('add_document', { namespace })))))) : null),
        isPrime &&
            directUploadsEnabled &&
            primeSubcontractors.map((subcontractor, idx) => (React.createElement(PrimeDocumentSection, { key: `${subcontractor.name}-${idx}`, subcontractor: subcontractor, columns: primeColumns, selectable: canPublish, selectedIds: selectedIds, onToggleSelect: onToggleSelect }))),
        isPrime &&
            !directUploadsEnabled &&
            groupedPrimeDocuments.map((subcontractor, idx) => (React.createElement(DocSection, { key: idx, subcontractor: subcontractor, onRequestDelete: onRequestDelete, onRequestScan: onRequestScan, onToggleShowOnReport: onToggleShowOnReport, idLoading: idLoading, canDelete: canDelete, documentType: documentsType[idx], onOpenDeficiencies: setDocumentDetail, aiEnabled: aiEnabled, isPrime: isPrime, onToggleSelect: onToggleSelect, selectedIds: selectedIds, canPublish: canPublish }))),
        isPrime && directUploadsEnabled && (React.createElement(PrimeAddDocumentsDrawer, { group: primeAddGroup, uploaderTenantId: primeAddTarget === null || primeAddTarget === void 0 ? void 0 : primeAddTarget.uploaderTenantId, parentType: parentType !== null && parentType !== void 0 ? parentType : '', parentId: parentId !== null && parentId !== void 0 ? parentId : 0, onClose: () => setPrimeAddTarget(null), onUploaded: refreshDocuments })),
        React.createElement(DeficienciesAi, { inspectionId: inspectionId !== null && inspectionId !== void 0 ? inspectionId : 0, document: documentDetail, onClose: onCloseBrushAway, onFinish: onFinishReviewDeficiencies }),
        confirmModalInfo && (React.createElement(ConfirmDialog, { title: confirmModalInfo.title, description: confirmModalInfo.description, onConfirm: confirmModalInfo.onConfirm, onCancel: closeConfirmModal })),
        React.createElement(UiConfirmDialog, { isOpen: showBulkPublishConfirm, title: translate('confirm_bulk_publish_title', { namespace }), description: React.createElement(React.Fragment, null,
                React.createElement("p", null, translate('confirm_bulk_publish', { namespace })),
                React.createElement(Radio, { name: "bulkPublishCategory", label: translate('publish_category_label', { namespace }), options: portalCategoryOptions, value: bulkPublishCategory, onChange: setBulkPublishCategory })), onConfirm: () => {
                setShowBulkPublishConfirm(false);
                void doBulkPublish();
            }, onCancel: () => {
                setShowBulkPublishConfirm(false);
                setBulkPublishCategory(DEFAULT_PORTAL_CATEGORY);
            } }),
        React.createElement(AddDocumentModal, { isOpen: !!addModalInfo, onAddDocument: onAddDocument, onClose: closeAddModal, documentType: addModalInfo === null || addModalInfo === void 0 ? void 0 : addModalInfo.documentType, saveUrl: inspectionId
                ? apiInternalInspectionDocumentsPath('', {
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    inspection_id: inspectionId,
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    uploader_tenant_id: addModalInfo === null || addModalInfo === void 0 ? void 0 : addModalInfo.subTenantId
                })
                : // eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/no-non-null-assertion
                    apiInternalWorkOrderDocumentsPath('', { work_order_id: workOrderId }) })));
}
export default DocumentsList;
DocumentsList.propTypes = {
    isPrime: PropTypes.bool,
    workOrderId: PropTypes.number,
    inspectionId: PropTypes.number,
    canDelete: PropTypes.bool,
    canCreate: PropTypes.bool,
    canPublish: PropTypes.bool
};
DocumentsList.defaultProps = {
    isPrime: false,
    workOrderId: undefined,
    inspectionId: undefined,
    canDelete: false,
    canCreate: false,
    canPublish: false
};
